// Description: Page to add an whatsapp business channel
// Updated: 28/08/2025
// Author: Simon Franklin

// React
import React, { useEffect, useState } from "react";

// Material UI Components
import {
  Box,
  Button,
  Divider,
  Typography,
  CircularProgress,
} from "@mui/material";

// Components and functions for the project
import HeaderPage from "../../components/HeaderPage/HeaderPage";
import StepIndicator from "../../components/DynamicForm/StepIndicator";
import StatusIcon from "../../components/StatusLabel/StatusIcon";
import { useNavigate } from "react-router-dom";
import { notify } from "../../utils/utils";
import { ChannelAddWppAPI } from "../../api/aws/Channels/ChannelAddWppAPI";
import themeColors from "../../components/ThemeColors/ThemeColors";
import CampaignModal from "../../components/Modals/CampaignModal";
import { isAuthenticated } from "../../authMiddleware";
import use123zapStore from "../../context/123zapStore";
import ReturnToLastPage from "../../components/ChannelAdd/ReturnToLastPage";
import { TextField } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const FormContent = React.memo(
  ({
    currentStep,
    steps,
    name,
    token,
    setName,
    setToken,
    url,
    setUrl,
    firstTime,
    success,
    setCurrentStep,
    copied,
    copyToClipboard,
    openModal,
    HandleWhatsApp,
  }) => {
    if (currentStep === 1) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>
        </Box>
      );
    } else if (currentStep === 2) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box sx={{ width: { xs: "100%", sm: "50%" }, margin: "0 auto" }}>
            <TextField
              value={name}
              placeholder="Nome de identificação"
              onChange={(e) => setName(e.target.value)}
              sx={{
                "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                  padding: "12px",
                },
                borderRadius: "6px",
                width: "100%",
              }}
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 3) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box sx={{ width: { xs: "100%", sm: "80%" }, margin: "0 auto" }}>
            <TextField
              value={url}
              placeholder="URL ofericida pela Meta"
              onChange={(e) => setUrl(e.target.value)}
              sx={{
                "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                  padding: "12px",
                },
                borderRadius: "6px",
                width: "100%",
              }}
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 4) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box sx={{ width: { xs: "100%", sm: "80%" }, margin: "0 auto" }}>
            <TextField
              value={token}
              placeholder="Token permanente"
              onChange={(e) => setToken(e.target.value)}
              sx={{
                "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                  padding: "12px",
                },
                borderRadius: "6px",
                width: "100%",
              }}
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 5) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                padding: "14px",
                borderRadius: "6px",
                backgroundColor: copied ? themeColors.green01 : "#f9f9f9",
                transition: ".5s ease-in-out",
              }}
            >
              https://dh9awq0282.execute-api.us-east-1.amazonaws.com/prod/webhook/whatsapp
            </Box>

            <Button
              variant="outlined"
              sx={{
                padding: "0 12px",
                marginTop: "10px",
                minWidth: {
                  xs: "100%",
                  sm: "0px",
                },
                height: "36px",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "9999px",
                fontSize: "16px",
                fontWeight: 500,
              }}
              onClick={copyToClipboard}
              startIcon={<ContentCopyIcon sx={{ color: "#000000ff" }} />}
            >
              {copied ? "Copiado" : "Copiar URL do Webhook"}
            </Button>
          </Box>
        </Box>
      );
    } else if (currentStep === 6 && firstTime > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              border: "2px solid #F5F3FF",
              borderRadius: "50%", // Garante que seja um círculo
              height: "200px", // Define o tamanho fixo para o círculo
              width: "200px", // Define a mesma largura para o círculo
              display: "flex",
              flexDirection: "column", // Organiza os itens em coluna
              justifyContent: "center", // Centraliza verticalmente
              alignItems: "center", // Centraliza horizontalmente
              textAlign: "center", // Alinhamento do texto centralizado
              position: "relative",
            }}
          >
            <CircularProgress
              sx={{
                position: "absolute", // Faz com que o progresso circular fique em cima do Box
                color: "#F5F3FF", // Cor do progresso circular
              }}
              size={200} // Tamanho do círculo
              thickness={1} // Espessura do progresso
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 7) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
          </Box>

          {success ? (
           <Button
                         variant="outlined"
                         sx={{
                           padding: "0 12px",
                           minWidth: {
                             xs: "100%",
                             sm: "0px",
                           },
                           height: "36px",
                           justifyContent: "center",
                           alignItems: "center",
                           borderRadius: "9999px",
                           fontSize: "16px",
                           fontWeight: 500,
                         }}
                         onClick={openModal}
                         startIcon={<CheckCircleOutlineIcon sx={{ color: "#000000ff" }} />}
                       >
                         Verificar meus canais
                       </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "10px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
                width: "100%", // Faz os botões ocuparem toda a largura
              }}
            >

            
            <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0px",
                  },
                  height: "36px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                onClick={HandleWhatsApp}
                startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
              >
                Falar com o suporte
              </Button>

              <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0px",
                  },
                  height: "36px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                onClick={() => {
                    setCurrentStep(1);
                }}
                startIcon={<ReplayIcon sx={{ color: "#000000ff" }} />}
              >
                Tentar novamente
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  }
);

const ChannelPageAddWhatsAppBusiness = () => {

  const [firstTime, setFirstTime] = useState(30);
  const [currentStep, setCurrentStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [copied, setCopied] = useState(false);
  const creatingCampaignWithHelpMode =
    JSON.parse(sessionStorage.getItem("creating_campaign")) || null;
  const [openModalCampaign, setOpenModalCampaign] = useState(false);
  const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
  const setAlreadyFetchChannels = use123zapStore(
    (state) => state.setAlreadyFetchChannels
  );
  const planoAtual = use123zapStore((state) => state.planoAtual);
  const userInfo = use123zapStore((state) => state.userInfo);
  const setUserInfo = use123zapStore((state) => state.setUserInfo);


  const Navigate = useNavigate();

  useEffect(() => {
    if (userAuthenticated && Object.keys(userInfo).length === 0) {
      const { decryptedData } = isAuthenticated();
      setUserInfo(decryptedData);
    }
  }, [userAuthenticated]);

  useEffect(() => {
    if (
      planoAtual !== "ProZap" &&
      planoAtual !== "UltraZap" &&
      planoAtual !== null
    ) {
      Navigate("/acesso-restrito");
    }
  }, [planoAtual]);

  const HandleChannels = () => {
    creatingCampaignWithHelpMode === true
      ? Navigate("/campanhas/adicionar")
      : Navigate("/canais");
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(
        "https://dh9awq0282.execute-api.us-east-1.amazonaws.com/prod/webhook/whatsapp"
      )
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Erro ao copiar: ", err));
  };

  const steps = [
    {
      stepNumber: 1,
      title: "Solicite o WhatsApp API",
      description: (
        <span>
          Antes de integrar o seu WhatsApp à 123zap, certifique-se de que a
          configuração na Meta/Facebook já tenha sido concluída. Caso tenha
          dúvidas,{" "}
          <a
            style={{
              color: themeColors.purple500,
              textDecoration: "none",
              fontWeight: 600,
            }}
            href="https://ajuda.bling.com.br/hc/pt-br/articles/13083445456663-Configurações-Passo-3-Crie-um-novo-aplicativo-empresarial-na-Meta"
            target="_blank" rel="noreferrer"
          >
            clique aqui
          </a>{" "}
          para acessar nossa documentação e seguir o passo a passo.
        </span>
      ),
    },
    {
      stepNumber: 2,
      title: "Agora, vamos integrar!",
      description: (
        <span>
          O primeiro passo é escolher um nome para o seu WhatsApp. Esse nome
          servirá apenas para identificação interna na plataforma e não será
          exibido para seus clientes durante as conversas.
        </span>
      ),
    },
    {
      stepNumber: 3,
      title: "Qual é a URL de acesso fornecida?",
      description: (
        <span>
          Informe, por gentileza, a URL fornecida pela Meta dentro do seu
          aplicativo. Essa URL será utilizada para o envio das mensagens através
          do canal configurado.
        </span>
      ),
    },
    {
      stepNumber: 4,
      title: "Informe o token permanente",
      description: (
        <span>
          O token é a chave que permite o envio de mensagens pelo seu canal.
          Certifique-se de inserir o token permanente, evitando a expiração que
          pode interromper suas campanhas e mensagens automáticas.
        </span>
      ),
    },
    {
      stepNumber: 5,
      title: "Configure nosso Webhook",
      description: (
        <span>
          Em seu aplicativo na Meta, você encontrará a seção de Webhooks.
          Para que a 123zap tenha acesso aos status das campanhas enviadas
          (Lidas, Falhadas e Entregues) configure o Webhook com a URL abaixo.
        </span>
      ),
    },
    {
      stepNumber: 6,
      title: "Aguarde, estamos aplicando as configurações",
      description: (
        <span>
          Por favor, aguarde enquanto aplicamos as configurações e finalizamos a
          integração. Isso pode levar alguns segundos.
        </span>
      ),
    },
    {
      stepNumber: 7,
      title: success
        ? "WhatsApp adicionado com sucesso!"
        : "Ops! Algo deu errado",
      description: success ? (
        <span>
          Seu canal de WhatsApp API foi{" "}
          <b style={{ color: "#8033F4" }}>adicionado</b> com sucesso!
          <br /> Clique no botão abaixo para verificar seus canais.
        </span>
      ) : (
        <span>
          Ocorreu um erro ao adicionar o canal WhatsApp API.
          <br /> Consulte nosso <b style={{ color: "#8033F4" }}>suporte</b> para
          ficar por dentro de todos os detalhes e orientações.
        </span>
      ),
    },
  ];

  const canMoveForward = () => {
    if (currentStep === 2 && name === "") {
      notify("Error", "Preencha o nome para identificação", true);
      return false;
    }
    if (currentStep === 3 && url === "") {
      notify("Error", "Insira a url oferecida pela Meta", true);
      return false;
    }
    if (currentStep === 4 && token === "") {
      notify("Error", "Digite o token", true);
      return false;
    }

    return true;
  };

 const handleAdvance = async () => {
  if (currentStep === 5) {
    setCurrentStep(6);
    setFirstTime(30);

    try {
      const response = await ChannelAddWppAPI({ url, token, name });

      if (response?.message === "success") {
        setSuccess(true);
        setAlreadyFetchChannels(false);
      } else {
        setSuccess(false);
      }
    } catch (error) {
      console.error("Erro ao chamar ChannelAddWppAPI:", error);
      setSuccess(false); // evita crash
    } finally {
      setFirstTime(0);
    }
  }

  setCurrentStep((prev) => prev + 1);
};

  useEffect(() => {
    if (firstTime === 0 && currentStep === 6) {
      setCurrentStep(7);
    }
  }, [firstTime, currentStep]);

  const HandleWhatsApp = () => {
     window.open(
      "https://wa.me/553121300297?text=Ol%C3%A1%2C%20estou%20precisando%20de%20suporte!%20",
      "_blank"
    );
    };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "100vh", sm: "100vh" },
        backgroundColor: { xs: "#fff", sm: "#F1F5F9" },
      }}
    >
      <ReturnToLastPage />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          opacity: 0.8,
          flex: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            id="form"
            sx={{
              background: "#FFF",
              minHeight: {
                xs: "100vh", // Ocupa a altura inteira em telas pequenas
                sm: "100%", // Altura personalizada em telas maiores
                md: "583.2px",
                lg: "583.2px",
                xl: "583.2px",
              },
              minWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              maxWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              padding: "30px",
              borderRadius: {
                xs: "0", // Sem borda arredondada em telas pequenas
                sm: ".5rem", // Bordas arredondadas em telas médias ou maiores
              },
              boxShadow: {
                xs: "none", // Sem sombra em telas pequenas
                sm: "0 2px 4px -1px rgba(0, 0, 0, 0.1)", // Sombra em telas maiores
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: {
                xs: "flex-start",
                sm: "flex-start",
                md: "space-between",
                lg: "space-between",
              },
            }}
          >
            <FormContent
              currentStep={currentStep}
              steps={steps}
              name={name}
              setName={setName}
              token={token}
              setToken={setToken}
              url={url}
              setUrl={setUrl}
              firstTime={firstTime}
              success={success}
              copied={copied}
              copyToClipboard={copyToClipboard}
              HandleWhatsApp={HandleWhatsApp}
                setCurrentStep={setCurrentStep}
              openModal={() =>
                creatingCampaignWithHelpMode === true
                  ? setOpenModalCampaign(true)
                  : Navigate("/canais")
              }
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: { xs: "0px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0",
                  },
                  height: "36px",
                  display:
                    currentStep === 1 || currentStep === 6 || currentStep === 7
                      ? "none"
                      : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginRight: "auto",
                  marginTop: { xs: "20px", sm: "0" },
                }}
                onClick={() => setCurrentStep((prevStep) => prevStep - 1)}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0", // Largura mínima em telas maiores
                  },
                  height: "36px",
                  display:
                    currentStep === 6 || currentStep === 7 ? "none" : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginTop: { xs: "20px", sm: "0" },
                }}
                onClick={() =>
                  canMoveForward() === true ? handleAdvance() : null
                }
              >
                Próximo
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <CampaignModal
        cancelText="Adicionar mais um canal"
        confirmText="Retornar ao formulário"
        subTitle={
          "Percebemos que você está configurando uma campanha. Gostaria de retornar ao formulário?"
        }
        isOpen={openModalCampaign}
        onClose={() => setOpenModalCampaign(false)}
        onCancel={() => Navigate("/canais/adicionar")}
        onConfirm={() => (
          HandleChannels(), sessionStorage.setItem("ReturToCampaign", true)
        )}
        title={"Canal adicionado com sucesso"}
        iconColor={themeColors.purple400}
      />
    </Box>
  );
};

export default ChannelPageAddWhatsAppBusiness;
